import type { ReactNode } from "react";

// A step in a left-to-right sequence, drawn as a chevron. Lifted out of the
// ASO Market slide's taxonomy ladder (2026-09-02) so the One-Pager's
// Past ▸ Present ▸ Future headers use the same shape. `tone` overrides the
// colour classes; `size` picks the type/padding scale. Defaults reproduce
// the market slide exactly.
export function ChevronStep({
  children,
  first,
  last,
  terminal,
  tone,
  stroke,
  size = "md",
}: {
  children: ReactNode;
  first?: boolean;
  last?: boolean;
  terminal?: boolean;
  tone?: string;
  /** Background class for a 2px outline — drawn as an outer clipped layer,
   *  since `clip-path` clips a CSS border away. */
  stroke?: string;
  size?: "sm" | "md" | "lg";
}) {
  const padding =
    size === "sm"
      ? first
        ? "py-1 pl-2.5 pr-4 md:py-1 md:pl-3.5 md:pr-6"
        : last
          ? "py-1 pl-4 pr-2.5 md:py-1 md:pl-6 md:pr-3.5"
          : "py-1 pl-4 pr-4 md:py-1 md:pl-6 md:pr-6"
      : size === "lg"
      ? first
        ? "py-1.5 pl-4 pr-6 md:py-2 md:pl-5 md:pr-8"
        : last
          ? "py-1.5 pl-6 pr-4 md:py-2 md:pl-8 md:pr-5"
          : "py-1.5 pl-6 pr-6 md:py-2 md:pl-8 md:pr-8"
      : first
        ? "py-1.5 pl-3 pr-5 md:py-2.5 md:pl-5 md:pr-8"
        : last
          ? "py-1.5 pl-5 pr-3 md:py-2.5 md:pl-8 md:pr-5"
          : "py-1.5 pl-5 pr-5 md:py-2.5 md:pl-8 md:pr-8";

  // -ml is set to POINT - 2 so a 2px hairline gap reveals the chevron seam
  const overlap = first ? "" : "-ml-[8px] md:-ml-[12px]";

  const color =
    tone ??
    (terminal
      ? "bg-blue-100 font-semibold text-blue-700"
      : "bg-slate-100 text-slate-700");

  const type =
    size === "lg"
      ? "text-sm font-bold uppercase tracking-[0.15em] md:text-base"
      : size === "sm"
        ? "text-xs md:text-sm"
        : "text-sm md:text-lg";

  // POINT = 10px (mobile), 14px (md). first = flat left + point right.
  // last = notch left + flat right. middle = notch left + point right.
  const clipMobile = first
    ? "[clip-path:polygon(0_0,calc(100%-10px)_0,100%_50%,calc(100%-10px)_100%,0_100%)]"
    : last
      ? "[clip-path:polygon(0_0,100%_0,100%_100%,0_100%,10px_50%)]"
      : "[clip-path:polygon(0_0,calc(100%-10px)_0,100%_50%,calc(100%-10px)_100%,0_100%,10px_50%)]";

  const clipMd = first
    ? "md:[clip-path:polygon(0_0,calc(100%-14px)_0,100%_50%,calc(100%-14px)_100%,0_100%)]"
    : last
      ? "md:[clip-path:polygon(0_0,100%_0,100%_100%,0_100%,14px_50%)]"
      : "md:[clip-path:polygon(0_0,calc(100%-14px)_0,100%_50%,calc(100%-14px)_100%,0_100%,14px_50%)]";

  const clip = `${clipMobile} ${clipMd}`;
  const face = (
    <div className={`flex items-center whitespace-nowrap ${type} ${padding} ${color} ${clip}`}>
      {children}
    </div>
  );

  if (!stroke) {
    return <div className={`${overlap} ${clip}`}>{face}</div>;
  }
  return (
    <div className={`p-[2px] ${overlap} ${stroke} ${clip}`}>{face}</div>
  );
}
